import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, Clock, MapPin, Loader2, AlertCircle, ShoppingCart } from 'lucide-react';
import { useBranding } from '../../context/BrandingContext';
import { staffApi } from '../../services/api';
import OfflineBanner from '../../components/OfflineBanner';
import StaffSellTicketsModal from '../components/StaffSellTicketsModal';

/**
 * Staff event detail screen:
 *  - Real-time per-tier availability (sold / capacity / remaining)
 *  - Big "Vendi biglietti" CTA → opens cash-sale modal
 *
 * Stats are refetched after every successful sale.
 */
export default function StaffEventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { primary_color } = useBranding();
  const pc = primary_color || '#D44E20';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sellOpen, setSellOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await staffApi.get(`/staff/events/${id}/availability`);
      setData(r.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return iso; }
  };

  const totalRemaining = useMemo(() => {
    if (!data?.tiers) return null;
    let any = false;
    let cap = 0;
    for (const t of data.tiers) {
      if (t.capacity === null || t.capacity === undefined) return null;
      any = true;
      cap += Math.max(0, t.capacity - t.sold);
    }
    return any ? cap : null;
  }, [data]);

  const allSoldOut = totalRemaining === 0;
  const canSell = data && data.event && !allSoldOut && data.event.is_active && data.event.ticketing_enabled;

  return (
    <div className="staff-app flex flex-col min-h-dvh bg-[#0E0E12]">
      <OfflineBanner />

      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3 animate-fade-in-up">
        <button
          onClick={() => navigate('/staff/events')}
          data-testid="staff-event-detail-back-btn"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-[#888] hover:text-white hover:bg-white/10 transition-all border border-white/5"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0">
          <p className="text-[#888888] text-xs">Dettaglio evento</p>
          <h1 className="text-white font-bold text-xl leading-tight truncate" data-testid="staff-event-detail-name">
            {data?.event?.name || '—'}
          </h1>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 pb-32 flex-1">
        {loading && (
          <div className="flex items-center justify-center py-20 text-[#888]">
            <Loader2 className="animate-spin mr-2" size={18} /> Caricamento…
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-300 flex items-start gap-3" data-testid="staff-event-detail-error">
            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Event meta card */}
            <div className="bg-[#18181E] border border-white/5 rounded-2xl p-4 mb-4">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[#bbb] text-sm">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={14} className="text-[#888]" />
                  {formatDate(data.event.event_date) || 'Data non impostata'}
                </span>
                {data.event.event_time && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={14} className="text-[#888]" /> {data.event.event_time}
                  </span>
                )}
                {data.event.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={14} className="text-[#888]" /> {data.event.location}
                  </span>
                )}
              </div>
              {data.event.is_past && (
                <p className="mt-2 text-amber-400 text-xs">Evento già passato — vendita ancora disponibile per il check-in tardivo.</p>
              )}
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-[#18181E] border border-white/5 rounded-2xl p-4 text-center" data-testid="staff-event-total-sold">
                <p className="text-[#888] text-[11px] uppercase tracking-wide">Venduti</p>
                <p className="text-white font-bold text-3xl tabular-nums mt-1">{data.tickets_sold_total}</p>
              </div>
              <div className="bg-[#18181E] border border-white/5 rounded-2xl p-4 text-center" data-testid="staff-event-total-checked">
                <p className="text-[#888] text-[11px] uppercase tracking-wide">Check-in</p>
                <p className="text-white font-bold text-3xl tabular-nums mt-1">{data.tickets_checked_in_total}</p>
              </div>
            </div>

            {/* Tiers */}
            <p className="text-[#888] text-sm font-medium mb-2">Disponibilità per fascia</p>
            <div className="space-y-2" data-testid="staff-event-tiers">
              {data.tiers.length === 0 && (
                <div className="bg-[#18181E] border border-white/5 rounded-2xl p-4 text-center text-[#888] text-sm">
                  Nessuna fascia configurata.
                </div>
              )}
              {data.tiers.map((t) => {
                const uncapped = t.capacity === null || t.capacity === undefined;
                const remaining = uncapped ? null : Math.max(0, t.capacity - t.sold);
                const fill = uncapped ? null : Math.min(100, (t.sold / Math.max(1, t.capacity)) * 100);
                const soldOut = !uncapped && remaining === 0;
                return (
                  <div
                    key={t.name}
                    data-testid={`staff-tier-row-${t.name}`}
                    className="bg-[#18181E] border border-white/5 rounded-2xl p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-white font-semibold text-sm truncate">{t.name}</p>
                        <p className="text-[#888] text-xs mt-0.5 tabular-nums">€ {t.price.toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold text-base tabular-nums">
                          {t.sold}{!uncapped && <span className="text-[#666] text-xs font-normal">/{t.capacity}</span>}
                        </p>
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: soldOut ? '#ef4444' : '#888' }}>
                          {uncapped ? 'venduti' : soldOut ? 'esaurito' : `${remaining} disp.`}
                        </p>
                      </div>
                    </div>
                    {!uncapped && (
                      <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${fill}%`, backgroundColor: soldOut ? '#ef4444' : pc }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Sticky CTA */}
      {!loading && !error && data && (
        <div className="fixed bottom-0 left-0 right-0 px-5 pb-6 pt-3 bg-gradient-to-t from-[#0E0E12] via-[#0E0E12] to-transparent">
          <button
            onClick={() => setSellOpen(true)}
            disabled={!canSell}
            data-testid="staff-open-sell-modal-btn"
            className="w-full h-14 rounded-2xl font-bold text-white text-base flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-all"
            style={{ backgroundColor: pc }}
          >
            <ShoppingCart size={18} />
            {allSoldOut ? 'Tutto esaurito' : 'Vendi biglietti'}
          </button>
        </div>
      )}

      {sellOpen && data && (
        <StaffSellTicketsModal
          eventId={id}
          event={data.event}
          tiers={data.tiers}
          onClose={() => setSellOpen(false)}
          onSold={() => { setSellOpen(false); load(); }}
        />
      )}
    </div>
  );
}
